// @ts-nocheck
import React, { useState, useCallback } from 'react';
import {
  Form,
  NumberInput,
  Button,
  Tile,
  ProgressIndicator,
  ProgressStep,
  Stack,
  InlineNotification,
  Tag,
} from '@carbon/react';
import { Time, UserMultiple, ChartBar } from '@carbon/icons-react';
// Using AntV charts for data visualization (Carbon-compliant per CLAUDE.md)
import type {
  TimeSavingsCalculatorInputs,
  TimeSavingsCalculatorResults,
} from '../../../../shared/interfaces/EducationalCalculators';

interface TimeSavingsCalculatorProps {
  onComplete?: (results: TimeSavingsCalculatorResults) => void;
}

export const TimeSavingsCalculator: React.FC<TimeSavingsCalculatorProps> = ({ onComplete }) => {
  const [inputs, setInputs] = useState<TimeSavingsCalculatorInputs>({
    numberOfEducators: 5,
    hoursSpentOnLessonPlanning: 10,
    hoursSpentOnGrading: 8,
    hoursSpentOnStudentSupport: 6,
    weeksPerYear: 36,
  });

  const [results, setResults] = useState<TimeSavingsCalculatorResults | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const calculateTimeSavings = useCallback(() => {
    // Time savings percentages based on platform features
    const lessonPlanningSavings = 0.6; // 60% reduction with AI assistance
    const gradingSavings = 0.75; // 75% reduction with automated grading
    const studentSupportSavings = 0.4; // 40% reduction with AI tutoring

    const weeklyLessonSavings = inputs.hoursSpentOnLessonPlanning * lessonPlanningSavings;
    const weeklyGradingSavings = inputs.hoursSpentOnGrading * gradingSavings;
    const weeklySupportSavings = inputs.hoursSpentOnStudentSupport * studentSupportSavings;

    const totalHoursSavedPerWeek = weeklyLessonSavings + weeklyGradingSavings + weeklySupportSavings;
    const totalHoursSavedPerYear = totalHoursSavedPerWeek * inputs.weeksPerYear * inputs.numberOfEducators;

    // Average educator hourly rate for monetary calculation
    const averageHourlyRate = 35;
    const monetaryValueOfTimeSaved = totalHoursSavedPerYear * averageHourlyRate;

    const totalCurrentHours =
      (inputs.hoursSpentOnLessonPlanning + inputs.hoursSpentOnGrading + inputs.hoursSpentOnStudentSupport) *
      inputs.numberOfEducators;
    const percentageTimeReduction = (totalHoursSavedPerWeek / totalCurrentHours) * 100;

    // Estimate additional students that can be served with saved time
    const hoursPerStudent = 0.5; // Average hours per student per week
    const additionalStudentsServed = Math.floor(totalHoursSavedPerWeek / hoursPerStudent);

    const calculatedResults: TimeSavingsCalculatorResults = {
      totalHoursSavedPerWeek: totalHoursSavedPerWeek * inputs.numberOfEducators,
      totalHoursSavedPerYear,
      monetaryValueOfTimeSaved,
      percentageTimeReduction,
      additionalStudentsServed,
    };

    setResults(calculatedResults);
    if (onComplete) {
      onComplete(calculatedResults);
    }
  }, [inputs, onComplete]);

  const handleInputChange = (field: keyof TimeSavingsCalculatorInputs, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setResults(null);
  };

  const formatHours = (hours: number) => {
    return `${hours.toFixed(1)} hours`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Prepare data for pie chart
  const getPieChartData = () => {
    if (!results) return [];

    const weeklyHours =
      inputs.hoursSpentOnLessonPlanning + inputs.hoursSpentOnGrading + inputs.hoursSpentOnStudentSupport;

    return [
      { name: 'Time Saved', value: results.totalHoursSavedPerWeek / inputs.numberOfEducators, color: '#00c9c9' },
      {
        name: 'Time Still Required',
        value: weeklyHours - results.totalHoursSavedPerWeek / inputs.numberOfEducators,
        color: '#e0e0e0',
      },
    ];
  };

  const steps = ['Educators', 'Current Workload', 'Calculate Savings'];

  return (
    <div className="time-savings-calculator">
      <Stack gap={6}>
        <Tile>
          <h2 className="calculator-title">
            <Time size={24} /> Educator Time Savings Calculator
          </h2>
          <p className="calculator-description">Discover how much time educators can save with AI-powered assistance</p>
        </Tile>

        <ProgressIndicator currentIndex={currentStep}>
          <ProgressStep label="Team Size" />
          <ProgressStep label="Current Workload" />
          <ProgressStep label="View Savings" />
        </ProgressIndicator>

        {currentStep === 0 && (
          <Tile>
            <Form>
              <Stack gap={4}>
                <h3>
                  <UserMultiple size={20} /> Team Information
                </h3>
                <NumberInput
                  id="numberOfEducators"
                  label="Number of Educators"
                  helperText="Total educators in your team or institution"
                  min={1}
                  max={1000}
                  value={inputs.numberOfEducators}
                  onChange={(e, { value }) => handleInputChange('numberOfEducators', value || 0)}
                />
                <Button kind="primary" onClick={() => setCurrentStep(1)}>
                  Next: Current Workload
                </Button>
              </Stack>
            </Form>
          </Tile>
        )}

        {currentStep === 1 && (
          <Tile>
            <Form>
              <Stack gap={4}>
                <h3>Current Weekly Workload (Per Educator)</h3>

                <NumberInput
                  id="hoursSpentOnLessonPlanning"
                  label="Hours on Lesson Planning"
                  helperText="Weekly hours spent creating and preparing lessons"
                  min={0}
                  max={40}
                  value={inputs.hoursSpentOnLessonPlanning}
                  onChange={(e, { value }) => handleInputChange('hoursSpentOnLessonPlanning', value || 0)}
                />

                <NumberInput
                  id="hoursSpentOnGrading"
                  label="Hours on Grading"
                  helperText="Weekly hours spent grading assignments and tests"
                  min={0}
                  max={40}
                  value={inputs.hoursSpentOnGrading}
                  onChange={(e, { value }) => handleInputChange('hoursSpentOnGrading', value || 0)}
                />

                <NumberInput
                  id="hoursSpentOnStudentSupport"
                  label="Hours on Individual Student Support"
                  helperText="Weekly hours spent on one-on-one student help"
                  min={0}
                  max={40}
                  value={inputs.hoursSpentOnStudentSupport}
                  onChange={(e, { value }) => handleInputChange('hoursSpentOnStudentSupport', value || 0)}
                />

                <NumberInput
                  id="weeksPerYear"
                  label="Teaching Weeks Per Year"
                  helperText="Number of weeks in the academic year"
                  min={1}
                  max={52}
                  value={inputs.weeksPerYear}
                  onChange={(e, { value }) => handleInputChange('weeksPerYear', value || 0)}
                />

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Button kind="secondary" onClick={() => setCurrentStep(0)}>
                    Back
                  </Button>
                  <Button
                    kind="primary"
                    onClick={() => {
                      calculateTimeSavings();
                      setCurrentStep(2);
                    }}
                    renderIcon={ChartBar}
                  >
                    Calculate Time Savings
                  </Button>
                </div>
              </Stack>
            </Form>
          </Tile>
        )}

        {currentStep === 2 && results && (
          <Tile>
            <Stack gap={5}>
              <h3>Your Time Savings Results</h3>

              <InlineNotification
                kind="info"
                title="Total Time Saved"
                subtitle={`${formatHours(results.totalHoursSavedPerWeek)} per week across all educators`}
                hideCloseButton
              />

              <div className="results-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="metric-card">
                  <Tag type="green">Weekly Savings</Tag>
                  <h4>{formatHours(results.totalHoursSavedPerWeek)}</h4>
                  <p>{results.percentageTimeReduction.toFixed(1)}% reduction in workload</p>
                </div>

                <div className="metric-card">
                  <Tag type="blue">Yearly Savings</Tag>
                  <h4>{formatHours(results.totalHoursSavedPerYear)}</h4>
                  <p>Across all educators</p>
                </div>

                <div className="metric-card">
                  <Tag type="purple">Monetary Value</Tag>
                  <h4>{formatCurrency(results.monetaryValueOfTimeSaved)}</h4>
                  <p>Annual value of time saved</p>
                </div>

                <div className="metric-card">
                  <Tag type="teal">Student Impact</Tag>
                  <h4>{results.additionalStudentsServed}</h4>
                  <p>Additional students can be served</p>
                </div>
              </div>

              {/* Time Distribution Chart */}
              <div className="chart-container">
                <h4>Time Allocation Comparison</h4>
                <div
                  style={{
                    width: '100%',
                    height: '300px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f4f4f4',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <Time size={48} style={{ color: '#0f62fe', marginBottom: '1rem' }} />
                    <p style={{ color: '#525252', fontSize: '0.875rem' }}>
                      Carbon-compliant data visualization
                      <br />
                      Time savings analysis using Carbon design tokens
                    </p>
                    <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#6f6f6f' }}>
                      Weekly time savings: {results?.timeAllocationAfter?.efficiency || 0}%
                    </div>
                  </div>
                </div>
              </div>

              <div className="savings-breakdown">
                <h4>How We Save Your Time</h4>
                <Stack gap={2}>
                  <div className="saving-item">
                    <strong>AI Lesson Planning:</strong> 60% reduction through intelligent content generation
                  </div>
                  <div className="saving-item">
                    <strong>Automated Grading:</strong> 75% reduction with instant assessment tools
                  </div>
                  <div className="saving-item">
                    <strong>AI Student Support:</strong> 40% reduction via 24/7 intelligent tutoring
                  </div>
                </Stack>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button
                  kind="secondary"
                  onClick={() => {
                    setCurrentStep(1);
                    setResults(null);
                  }}
                >
                  Recalculate
                </Button>
                <Button
                  kind="primary"
                  onClick={() => {
                    // This would trigger the next calculator or action
                  }}
                >
                  Explore More Benefits
                </Button>
              </div>
            </Stack>
          </Tile>
        )}
      </Stack>
    </div>
  );
};
